import type { ReactNode } from 'react'

/**
 * Pure presentational movie card. No hooks, no data fetching, no
 * platform-specific imports — every dynamic value comes in as a prop,
 * so the same component renders identically in the PWA (Firestore +
 * lazy TMDB fetch) and in a future Electron renderer (SQLite + local
 * cache).
 *
 * The PWA wraps this with `useTmdbLazyMovie` to feed it title / year /
 * poster / TMDB rating; the desktop will eventually wrap it with its
 * own SQLite-backed equivalent. Both call sites pass `cardRef` if they
 * want to attach an IntersectionObserver for lazy loading.
 *
 * Visual conventions (kept consistent with the rest of the Govu UI):
 *   - Year shown in gold (accent-gold)
 *   - User rating badge in gold (top-left)
 *   - TMDB rating badge in blue, only if user hasn't rated
 *   - Gradient title overlay along the bottom of the poster
 *   - Owner labels ("On <device name>") in the bottom strip when the
 *     entry is non-virtual (i.e. some device has the file locally)
 */
export interface MovieCardViewProps {
  title: string
  year: number | null
  poster: string | null
  /** TMDB vote average. Shown only if userRating is null. */
  tmdbRating: number
  /** User rating, 1-10. Null when the user hasn't rated this title. */
  userRating: number | null
  /**
   * `true` if the entry exists only as a cloud / streaming-only
   * record. `false` means at least one device has the local file.
   */
  isVirtual: boolean
  /**
   * Pre-formatted device labels, e.g. ["MacBook", "Living Room PC"].
   * Empty array → "On another device" fallback. Ignored when
   * `isVirtual` is true.
   */
  ownerNames: string[]
  /** True while TMDB metadata is still resolving. */
  loading: boolean
  onOpen: () => void
  /**
   * Optional callback ref forwarded to the outer wrapper so the
   * caller can attach an IntersectionObserver for lazy poster fetch.
   */
  cardRef?: (el: HTMLDivElement | null) => void
}

export function MovieCardView({
  title,
  year,
  poster,
  tmdbRating,
  userRating,
  isVirtual,
  ownerNames,
  loading,
  onOpen,
  cardRef,
}: MovieCardViewProps) {
  return (
    <div ref={cardRef} className="group flex flex-col">
      <button
        type="button"
        onClick={onOpen}
        className="relative block aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03] group-hover:shadow-cinematic-lg"
      >
        {poster ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={poster}
            alt={title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <PlaceholderPoster label={title} loading={loading} />
        )}

        {userRating != null ? (
          <Badge className="bg-accent-gold/80 text-black">
            <Star /> {userRating}
          </Badge>
        ) : tmdbRating > 0 ? (
          <Badge className="bg-blue-500/80 text-white">
            <Star /> {tmdbRating.toFixed(1)}
          </Badge>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 pt-8">
          <p className="line-clamp-2 text-sm font-medium leading-tight text-white drop-shadow-lg">
            {title}
          </p>
          <div className="mt-0.5 flex items-center gap-2 text-[11px]">
            {year && <span className="font-medium text-accent-gold drop-shadow">{year}</span>}
            {!isVirtual && (
              <span className="truncate text-white/80 drop-shadow">
                {ownerNames.length > 0
                  ? `On ${ownerNames.join(', ')}`
                  : 'On another device'}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  )
}

function Badge({
  children,
  className,
}: {
  children: ReactNode
  className: string
}) {
  return (
    <div
      className={`absolute left-2 top-2 flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold shadow-sm backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  )
}

function Star() {
  return <span className="opacity-75">★</span>
}

function PlaceholderPoster({ label, loading }: { label: string; loading: boolean }) {
  return (
    <div className="grid h-full w-full place-items-center px-2 text-center">
      <div className="space-y-2">
        {loading ? (
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-text-tertiary border-t-accent-primary" />
        ) : (
          <div className="mx-auto text-3xl opacity-60">🎬</div>
        )}
        <span className="line-clamp-2 text-xs text-text-tertiary">{label}</span>
      </div>
    </div>
  )
}
